import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";

import { selectOptions, setFluid } from "../reducers/optionsSlice";
import { selectLists } from "../reducers/listSlice";
import { selectWells } from "../reducers/wellSlice";
import { selectTypeCurves } from "../reducers/typeCurveSlice";

import { ForecastDataTable } from "./ForecastDataTable";
import { formatFluid } from "../helpers/formatFluid";
import { formatNumber } from "../helpers/formatNumber";
import { getUnits } from "../helpers/getUnits";
import { ProductionChartOptions } from "./ProductionChartOptions";
import { FilterWellList } from "./FilterWellList";
import { AccessTime, CloudUpload, ExpandMore, Warning } from "@mui/icons-material";
import { convertVolume } from "../helpers/convertVolume";
import { addProject, selectProjects } from "../reducers/projectSlice";
import { useSnackbar } from "notistack";
import { selectGroupWells } from "../reducers/groupWellSlice";
import { normalizeVolume } from "../helpers/normalizeVolume";
import { selectUser } from "../reducers/userSlice";
import { WaterfloodContainer } from "./WaterfloodContainer";
import { ProductionChartNew } from "./ProductionChartNew";
import { saveEconomics, selectEconomics } from "../reducers/economicsSlice";
import { selectFBA } from "../reducers/fbaSlice";
import { useSocket } from "../context/useSocket";

const MONTH_LENGTH = 30.436875;

const useStyles = makeStyles(theme => ({
  root: {
    border: 0,
    color: 'text.primary',
    overflowY: 'auto',
    height: '100%',
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    minWidth: 600,
    padding: 0,
    paddingTop: 10,
    margin: 0,
    marginTop: 10,
  },
  header: {
    padding: '0 10px',
    paddingTop: 10,
    position: 'relative'
  },
  cards: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  card: {
    margin: '0 10px',
    height: 130,
    padding: 10,
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: 400,
  },
  button: {
    borderRadius: '50%'
  },
  status: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 10
  },
  icon: {
    color: theme.palette.text.secondary
  }
}));

export const Forecast = () => {

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();

  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects.byId[projects.currentProjectId];
  const options = useSelector(selectOptions);
  const lists = useSelector(selectLists);
  const list = lists?.byId[lists?.currentListId];
  const wells = useSelector(selectWells);
  const typeCurves = useSelector(selectTypeCurves);
  const typeCurve = typeCurves?.byId[list?.typeCurveId];
  const groupWells = useSelector(selectGroupWells);
  const economics = useSelector(selectEconomics);
  const fba = useSelector(selectFBA);

  const wellUwi = wells?.currentWellUwi || "";
  const well = (list?.forecastListTab === 0 || list?.forecastListTab === 3) ? wells?.byUwi?.[wellUwi] : list?.forecastListTab === 1 ? typeCurves?.byId?.[list?.typeCurveId] : groupWells?.byId?.[list?.groupWellId];

  const staleData = well?.production?.staleData;

  const dispatch = useDispatch();

  const { socket } = useSocket();

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';

  const [isLoading, setIsLoading] = useState(false);

  const [tabItem, setTabItem] = useState(() => getTabItemFromFluid(options.fluid));

  useEffect(() => {
    let active = true;

    if (active && well?.status === 'processed' && !well?.production?.byFluid[options?.fluid] && !well?.forecast?.byFluid[options?.fluid]) {
      const newFluid = well?.production?.fluids?.[0];
      dispatch(setFluid({fluid: newFluid}));
      setTabItem(() => getTabItemFromFluid(newFluid));
    };

    return () => active = false;
  }, [dispatch, options?.fluid, well]);

  return (
    <div className={classes.root} id="forecast" style={{backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light']}}>
      {well && 
      <Box style={{boxShadow: `0px 2px 6px ${darkMode ? '#222' : 'lightgrey'}`}}>
        <div className={classes.header} style={{backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light}}>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <Typography variant="h6" color="textSecondary" style={{fontSize: 14}}>
              {`${formatFluid(options?.fluid) && formatFluid(options?.fluid)?.toUpperCase()} PRODUCTION FORECAST`}
            </Typography>
          </div>

          <div style={{paddingLeft: 10, paddingBottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'absolute', top: 10, right: 10, backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light}}>
            
            <div style={{position: 'relative'}}>
              <Tooltip title="Save Project">
                <span><Button 
                  startIcon={<CloudUpload />} 
                  size="small"
                  variant="contained" 
                  style={{color: darkMode ? '#000' : '#fff', height: 28}}
                  color={darkMode ? "inherit" : "primary"}
                  edge="end" 
                  onClick={() => handleSaveProject(currentProject)}
                  disabled={demoUser || isLoading || user?.value?.email !== projects?.byId[projects?.currentProjectId]?.createdBy}
                >
                  Save Project
                </Button></span>              
              </Tooltip>

              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-11px',
                    marginLeft: '-11px',
                  }}
                />
              )}
            </div>

            <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
              <AccessTime color="textSecondary" fontSize="14px" style={{ marginRight: 5 }} />
              <Typography color="textSecondary" fontSize="14px" textAlign={'right'}>Last saved: {new Date(currentProject?.lastModified)?.toLocaleString()}</Typography>
            </div>
          </div>
          
          <div style={{display: 'flex', alignItems: 'center'}}>
            <Tooltip title="Data is more than 2 weeks old!">
              <span style={{display: 'flex', alignItems: 'center'}}><Warning color="secondary" style={{display: !staleData && 'none'}} /></span>
            </Tooltip>
            <Typography noWrap variant="h5" style={{marginTop: '0px'}}>
              {list?.forecastListTab === 0 ? well?.formattedUwi : well?.name}
            </Typography>
          </div>
          <Typography color="textSecondary" style={{fontSize: '18px'}}>
            {`${well?.details?.well_name || well?.details?.bottom_hole_location || ""}`}
          </Typography>
          <Typography color="textSecondary" style={{fontSize: '14px', paddingBottom: 5, paddingTop: 5}}>
            {well?.copy ? "New Drill" : well?.local ? "Imported Well" : 
            (list?.forecastListTab === 0 && well?.details) && `${well?.details?.well_status || well?.details?.status || well?.details?.well_mode || ""} | ${well?.details?.well_type || well?.details?.status_full || well?.details?.well_operation || ""} | ${well?.details?.field_name || well?.details?.well_area_name || well?.details?.pool || ""} | ${well?.details?.licensee || well?.details?.licensee_name || ""}`}
          </Typography>

        </div>
        <Tabs
          className={classes.tabs}
          value={tabItem}
          TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
          textColor={darkMode ? "inherit" : "primary"}
          onChange={handleTabChange}
          style={{
            backgroundColor: (darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light),
            borderBottom: `1px solid ${darkMode ? 'dimgrey' : 'lightgrey'}`,
          }}
        >
          <Tab label="Oil" disabled={!well?.production?.byFluid?.oil && !well?.forecast?.byFluid?.oil} />
          <Tab label="Gas" disabled={!well?.production?.byFluid?.gas && !well?.forecast?.byFluid?.gas} />
          <Tab label="Condensate" disabled={!well?.production?.byFluid?.condensate && !well?.forecast?.byFluid?.condensate} />
          <Tab label="Water" disabled={!well?.production?.byFluid?.water && !well?.forecast?.byFluid?.water} />
          <Tab label="Pentanes Plus" disabled={!well?.production?.byFluid?.pentanesPlus && !well?.forecast?.byFluid?.pentanesPlus} />
        </Tabs>
      </Box>}


      {
        options.fluid === 'oil' && well?.params?.oil?.useWaterflood ?
        
      <WaterfloodContainer well={well} />
      :
      (((well?.production?.byFluid?.[options?.fluid]?.length > 0 || well?.forecast?.byFluid?.[options?.fluid]?.length > 0)) ?
          <>
            <Accordion 
              defaultExpanded 
              className={classes.content}
              sx={{boxShadow: 1}}
              style={{
                margin: 10,
                borderRadius: 4,
                border: 'none',
                backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
              }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography noWrap>Production Chart</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <ProductionChartOptions list={list} groupWell={well} darkMode={darkMode} userEmail={user?.value?.email} />
                <div style={{height: options?.chartOrientation === 'Horizontal' ? '500px' : '1000px'}}>
                  <ProductionChartNew />
                </div>
              </AccordionDetails>
            </Accordion>
            
            <Accordion 
              defaultExpanded
              className={classes.content}
              sx={{boxShadow: 1}}
              style={{
                margin: 10,
                borderRadius: 4,
                border: 'none',
                backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
              }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
                <Typography noWrap>{options?.normalization !== 'off' ? 'Normalized' : ''} {options?.fluid?.charAt(0)?.toUpperCase()?.concat(options?.fluid?.substring(1, ))} Forecast Summary</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <EurContainer />
              </AccordionDetails>
            </Accordion>
    
            <Accordion 
              defaultExpanded
              className={classes.content}
              sx={{boxShadow: 1}}
              style={{
                margin: 10,
                borderRadius: 4,
                border: 'none',
                backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
              }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography noWrap>{options?.normalization !== 'off' ? 'Normalized' : ''} {options?.fluid?.charAt(0)?.toUpperCase()?.concat(options?.fluid?.substring(1, ))} Forecast Data</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <ForecastDataTable 
                  well={well} 
                  fluid={getFluidFromTabItem(tabItem)} 
                  units={options.units} 
                  normalization={options.normalization} 
                  typeCurve={typeCurve}
                  typeCurveCurrent={list.forecastListTab === 1}
                />
              </AccordionDetails>
            </Accordion>
          </>
          :
          <div style={{width: '100%', height: '90%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <Typography variant = "h6" color="textSecondary">Production not available</Typography>
          </div>
        )
      }
      
      {options?.showWellListFilter && <FilterWellList listId={options?.showWellListFilter} wells={list.wellUwis.map(uwi => wells.byUwi[uwi])} />}

    </div>
  );


  // FUNCTIONS
  function handleSaveProject(project) {
    try {
      if (!project) return;

      console.log('Saving project ' + project.name);

      setIsLoading(true);

      let newWells = project?.wells?.length > 0 ? [...project.wells] : [];

      // save all project economics
      const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === currentProject?.id && economics?.byId?.[id]?.cashflow); // only keep economicsIds that correspond to the current project
      for (let economicsId of economicsIds) {
        if (economics?.byId?.[economicsId]?.forecastNet && economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.cashflowGross) {
          dispatch(saveEconomics(economics?.byId?.[economicsId]));
        };
      };

      // save all project fbas
      const fbaIds = fba?.allIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
      if (fbaIds?.length > 0 && socket && socket.readyState === 1) {
        for (let fbaId of fbaIds) {
          const currFBA = fba?.byId?.[fbaId];
          if (currFBA?.fbaData) {

            socket.send(JSON.stringify({
              messageType: 'saveFBA', 
              fba: currFBA, 
              fileName: currFBA?.id, 
              userEmail: user?.value?.email
            }));

          };
        };
      };

      // compile a list of UWIs in the project
      let projectUwis = [];

      project.listIds.forEach(listId => {
        lists.byId[listId]?.wellUwis?.forEach(wellUwi => {
          projectUwis.push(wellUwi);
        });
      });

      for (let wellUwi of projectUwis) { // save local params
        try {
          if (wells?.byUwi?.[wellUwi]?.status === 'processed' && wells?.byUwi?.[wellUwi]?.params && !wells?.byUwi?.[wellUwi]?.local) { // if params are available, well is processed
            let wellParams = {...wells?.byUwi?.[wellUwi]?.params};
            const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
    
            for (let fluid of wellFluids) {
              wellParams[fluid] = {...wellParams?.[fluid], outliers: []};
            };

            const wellIndex = newWells.map(w => w.uwi)?.indexOf(wellUwi);
            if (wellIndex === -1) { // if well hasnt been saved before, add it
              newWells.push({uwi: wellUwi, params: wellParams});
            } else { // update existing saved well with new params
              newWells.splice(wellIndex, 1, {uwi: wellUwi, params: wellParams});
            };
          };
          
        } catch (error) {
          console.log(error);
        };
      };

      const newProject = {
        id: project?.id,
        name: project?.name, 
        color: project?.color,
        description: project?.description || "",
        createdBy: project?.createdBy,
        lastModified: new Date().toLocaleString(),
        createdOn: project?.createdOn,
        listIds: project?.listIds || [],
        wells: newWells,
        currentListId: project?.currentListId || "",
        economicsIds: economicsIds?.length > 0 ? economicsIds : (project?.economicsIds || []),
        fbaIds: fbaIds?.length > 0 ? fbaIds : (project?.fbaIds || []),
        public: project?.public || false
      };

      const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
        const currentList = lists.byId[listId];
        return {
          id: listId,
          name: currentList?.name,
          createdBy: currentList?.createdBy,
          lastModified: new Date().toLocaleString(),
          color: currentList?.color,
          description: currentList?.description || "",
          wellUwis: currentList?.wellUwis.filter(uwi => !wells.byUwi[uwi]?.local),
          typeCurveId: currentList?.typeCurveId,
          groupWellId: currentList?.groupWellId,
          forecastListTab: currentList?.forecastListTab,
          filters: currentList?.filters
        };
      }) : [];
      dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
        setIsLoading(false);
        enqueueSnackbar(`Project ${project?.name} saved successfully`, { variant: 'success' });
      });
    } catch (error) {
      setIsLoading(false);
      console.log(error);
      return;
    };
    
    
  };

  function getFluidFromTabItem(tabItem) {
    switch(tabItem) {
      case 0:
        return 'oil';
      case 1:
        return 'gas';
      case 2:
        return 'condensate';
      case 3:
        return 'water';
      case 4:
        return 'pentanesPlus';
      default:
        return '';
    };
  };

  function handleTabChange(e, value) {
    e.preventDefault();
    dispatch(setFluid({fluid: getFluidFromTabItem(value)}));
    setTabItem(value);
  };

  function EurContainer() {
    try {
      const dateP90 = new Date(well?.eur?.byFluid?.[options?.fluid]?.p90?.date).toDateString().split(' ');
      const dateP50 = new Date(well?.eur?.byFluid?.[options?.fluid]?.p50?.date).toDateString().split(' ');
      const dateP10 = new Date(well?.eur?.byFluid?.[options?.fluid]?.p10?.date).toDateString().split(' ');
      let size = 'h4';

      if (well?.eur?.byFluid?.[options?.fluid]?.p10?.cumVolume > 100000) {
        size = 'h5';
      } else if (well?.eur?.byFluid?.[options?.fluid]?.p10?.cumVolume > 1000000) {
        size = 'h6';
      };

      return (
        <div className={classes.cards} id="eur-cards" style={{flexDirection: 'row'}}>
        {/* <div className={classes.cards} id="eur-cards" style={{flexDirection: getFlexDirection(well?.eur?.byFluid?.[options?.fluid]?.p10?.cumVolume)}}> */}
          <div className={classes.card}>
            <Typography variant="body2">EUR P90</Typography>
            {well?.eur?.byFluid?.[options?.fluid] && 
            <>
              <Typography variant={size} align='left' style={{marginTop: 10}}>
                <span><b>{returnEur('p90')}</b></span> <span style={{fontSize: size === 'h4' ? 24 : size === 'h5' ? 20 : 16}}><b>{getUnits(options)}</b></span>
              </Typography>
              <Typography variant="caption" align='left' style={{marginTop: 20, fontSize: 12}}>
                {`${dateP90[1] || ""}-${dateP90[3] || ""} @${(convertVolume(well?.params?.cutoff, options.units, options.fluid) / MONTH_LENGTH)?.toFixed(2) || ""} ${getUnits(options)}/day`}
              </Typography>
              <Typography variant="caption" style={{display: 'block'}}>Remaining: {calculateRemaining('p90')} {getUnits(options)}</Typography>
            </>}

          </div>
          <div className={classes.card}>
            <Typography variant="body2">EUR P50</Typography>
            {well?.eur?.byFluid?.[options?.fluid] && 
            <>
              <Typography variant={size} align='left' style={{marginTop: 10}}>
                <span><b>{returnEur('p50')}</b></span> <span style={{fontSize: size === 'h4' ? 24 : size === 'h5' ? 20 : 16}}><b>{getUnits(options)}</b></span>
              </Typography>
              <Typography variant="caption" align='left' style={{marginTop: 20, fontSize: 12}}>
                {`${dateP50[1] || ""}-${dateP50[3] || ""} @${(convertVolume(well?.params?.cutoff, options.units, options.fluid) / MONTH_LENGTH)?.toFixed(2) || ""} ${getUnits(options)}/day`}
              </Typography>
              <Typography variant="caption" style={{display: 'block'}}>Remaining: {calculateRemaining('p50')} {getUnits(options)}</Typography>
            </>}
          </div>
          <div className={classes.card}>
            <Typography variant="body2">EUR P10</Typography>
            {well?.eur?.byFluid?.[options?.fluid] && 
            <>
              <Typography variant={size} align='left' style={{marginTop: 10}}>
                <span><b>{returnEur('p10')}</b></span> <span style={{fontSize: size === 'h4' ? 24 : size === 'h5' ? 20 : 16}}><b>{getUnits(options)}</b></span>
              </Typography>
              <Typography variant="caption" align='left' style={{marginTop: 20, fontSize: 12}}>
                {`${dateP10[1] || ""}-${dateP10[3] || ""} @${(convertVolume(well?.params?.cutoff, options.units, options.fluid) / MONTH_LENGTH)?.toFixed(2) || ""} ${getUnits(options)}/day`}
              </Typography>
              <Typography variant="caption" style={{display: 'block'}}>Remaining: {calculateRemaining('p10')} {getUnits(options)}</Typography>
            </>}
          </div>
        </div>
      ); 
      
      // FUNCTIONS
      function returnEur(prob) {
        try {
          if (options?.normalization !== 'off' && (!well.completions || !typeCurve?.details)) return "-";
          let eur = well?.eur?.byFluid?.[options?.fluid]?.[prob]?.cumVolume;
          eur = convertVolume(eur, options.units, options.fluid);
          eur = normalizeVolume(eur, options?.normalization, typeCurve?.details, well?.completions);
          eur = formatNumber(eur.toFixed(1));
          return eur;
        } catch (error) {
          return "-";
        };
      };

      function calculateRemaining(prob) {
        try {
          if (options?.normalization !== 'off' && (!well.completions || !typeCurve?.details)) return "-";
          const eur = well?.eur?.byFluid?.[options?.fluid]?.[prob]?.cumVolume;
          const cumToDate = well?.production?.byFluid?.[options?.fluid]?.slice(-1)?.[0]?.cumVolume;

          let remaining = eur - cumToDate;

          remaining = convertVolume(remaining, options.units, options.fluid);
          remaining = normalizeVolume(remaining, options?.normalization, typeCurve?.details, well?.completions);

          if (isNaN(remaining)) return 0;

          remaining = formatNumber(Math.max(remaining, 0).toFixed(1));
          
          return remaining;
        } catch (error) {
          return "-";
        };
      };
      
    } catch (error) {
      return <></>;
    };
  };

};

// function getFlexDirection(eurP10) {
//   if (document?.getElementById('eur-cards')?.getBoundingClientRect()?.width < 1200 || eurP10 > 100000) return 'column';
//   return 'row';
// };

function getTabItemFromFluid(fluid) {
  switch(fluid) {
    case 'oil':
      return 0;
    case 'gas':
      return 1;
    case 'condensate':
      return 2;
    case 'water':
      return 3;
    case 'pentanesPlus':
      return 4;
    default:
      return '';
  };
};


